import React, { useState, useEffect } from 'react';
import { Card, Button } from '@/components/ui';
import { Pagination } from '@/components/common/Pagination';
import { useLanguage } from '@/state/language/LanguageProvider';
import { navigationManagementService, NavigationHierarchy } from '@/services/navigationManagement';
import { toast } from 'react-hot-toast';

const NavigationHierarchyPage: React.FC = () => {
  const { t } = useLanguage();
  const [navigationHierarchy, setNavigationHierarchy] = useState<NavigationHierarchy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    loadNavigationHierarchy();
  }, []);

  const loadNavigationHierarchy = async () => {
    try {
      setLoading(true);
      const hierarchyData = await navigationManagementService.getFullHierarchy();
      setNavigationHierarchy(hierarchyData);
      setTotalItems(hierarchyData.length);
      setError(null);
    } catch (err) {
      setError('Failed to load navigation hierarchy');
      console.error('Error loading navigation hierarchy:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateModule = async (moduleId: string, moduleName: string) => {
    try {
      await navigationManagementService.deactivateModule(moduleId);
      toast.success(t('navigation.moduleDeactivatedSuccess', { moduleName }));
      loadNavigationHierarchy(); // Reload to reflect changes
    } catch (error) {
      toast.error(t('navigation.failedToDeactivateModule', { moduleName }));
      console.error('Error deactivating module:', error);
    }
  };

  const handleDeleteModule = async (moduleId: string, moduleName: string) => {
    if (!window.confirm(t('navigation.confirmDeleteModule', { moduleName }))) {
      return;
    }
    
    try {
      await navigationManagementService.deleteModule(moduleId);
      toast.success(t('navigation.moduleDeletedSuccess', { moduleName }));
      loadNavigationHierarchy(); // Reload to reflect changes
    } catch (error) {
      toast.error(t('navigation.failedToDeleteModule', { moduleName }));
      console.error('Error deleting module:', error);
    }
  };

  const handleDeactivateScreen = async (screenId: string, screenName: string) => {
    try {
      await navigationManagementService.deactivateScreen(screenId);
      toast.success(t('navigation.screenDeactivatedSuccess', { screenName }));
      loadNavigationHierarchy(); // Reload to reflect changes
    } catch (error) {
      toast.error(t('navigation.failedToDeactivateScreen', { screenName }));
      console.error('Error deactivating screen:', error);
    }
  };

  const handleDeleteScreen = async (screenId: string, screenName: string) => {
    if (!window.confirm(t('navigation.confirmDeleteScreen', { screenName }))) {
      return;
    }
    
    try {
      await navigationManagementService.deleteScreen(screenId);
      toast.success(t('navigation.screenDeletedSuccess', { screenName }));
      loadNavigationHierarchy(); // Reload to reflect changes
    } catch (error) {
      toast.error(t('navigation.failedToDeleteScreen', { screenName }));
      console.error('Error deleting screen:', error);
    }
  };

  const handleDeleteNode = async (nodeId: string, nodeName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${nodeName}"?`)) {
      return;
    }
    
    try {
      await navigationManagementService.deleteNode(nodeId);
      toast.success(`Node "${nodeName}" deleted successfully`);
      await loadNavigationHierarchy();
    } catch (error) {
      console.error('Error deleting node:', error);
      toast.error(`Failed to delete node "${nodeName}": ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleLocationChange = async (screenId: string, newLocation: string) => {
    try {
      await navigationManagementService.updateScreenLocation(screenId, newLocation);
      toast.success(`Location updated to ${newLocation}`);
      loadNavigationHierarchy();
    } catch (error) {
      toast.error(`Failed to update location: ${error instanceof Error ? error.message : 'Unknown error'}`);
      console.error('Error updating location:', error);
    }
  };

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setItemsPerPage(size);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Calculate paginated data
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedHierarchy = navigationHierarchy.slice(startIndex, endIndex);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-secondary-font">{t('navigation.loadingNavigationHierarchy')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-red-600">{t('navigation.failedToLoadNavigationHierarchy')}</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-primary-font mb-2">
          {t('navigation.navigationHierarchy')}
        </h1>
        <p className="text-secondary-font">
          {t('navigation.navigationHierarchyDescription')}
        </p>
      </div>

      <Card className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-primary-font">{t('navigation.navigationStructure')}</h2>
          <div className="flex space-x-2">
            <Button onClick={() => toast.success('Add Module feature coming soon')}>{t('navigation.addModule')}</Button>
            <Button onClick={() => toast.success('Add Screen feature coming soon')}>{t('navigation.addScreen')}</Button>
            <Button onClick={() => toast.success('Add Node feature coming soon')}>{t('navigation.addNode')}</Button>
          </div>
        </div>
        
                 {/* Navigation Hierarchy Tree */}
         <div className="space-y-4">
           {paginatedHierarchy
             .sort((a, b) => a.display_order - b.display_order)
             .map((module) => (
            <div key={module.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-primary-font">{module.name}</h3>
                <div className="flex space-x-2">
                  <button 
                    onClick={() => toast.success(`Edit Module: ${module.name} (Feature coming soon)`)}
                    className="text-blue-600 hover:text-blue-900 text-sm"
                  >
                    {t('navigation.edit')}
                  </button>
                  <div className="relative group">
                    <button className="text-gray-600 hover:text-gray-900 text-sm px-2 py-1 border border-gray-300 rounded">
                      {t('navigation.actions')} ▼
                    </button>
                    <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-300 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                      <div className="py-1">
                        <button 
                          onClick={() => toast.success(`Edit Module: ${module.name} (Feature coming soon)`)}
                          className="block w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50"
                        >
                          {t('navigation.edit')}
                        </button>
                        <button 
                          onClick={() => handleDeactivateModule(module.id, module.name)}
                          className="block w-full text-left px-4 py-2 text-sm text-orange-600 hover:bg-orange-50"
                        >
                          {t('navigation.makeInactive')}
                        </button>
                        <button 
                          onClick={() => handleDeleteModule(module.id, module.name)}
                          className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          {t('navigation.hardDelete')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
                             <p className="text-sm text-secondary-font mb-3">{module.description}</p>
               <p className="text-xs text-gray-500">{t('navigation.order')}: {module.display_order}</p>
              
                             {/* Screens */}
               <div className="ml-4 space-y-2">
                 {Object.values(module.screens)
                   .sort((a, b) => a.display_order - b.display_order)
                   .map((screen) => (
                  <div key={screen.id} className="border-l-2 border-gray-300 pl-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-primary-font">{screen.name}</h4>
                      <div className="flex space-x-2">
                        <div className="relative group">
                          <button className="text-blue-600 hover:text-blue-900 text-sm px-2 py-1 border border-gray-300 rounded">
                            {t('navigation.editLocation')} ▼
                          </button>
                          <div className="absolute left-0 mt-1 w-32 bg-white border border-gray-300 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                            <div className="py-1">
                              <button 
                                onClick={() => handleLocationChange(screen.id, 'SIDEBAR')}
                                className="block w-full text-left px-3 py-1 text-sm text-blue-600 hover:bg-blue-50"
                              >
                                SIDEBAR
                              </button>
                              <button 
                                onClick={() => handleLocationChange(screen.id, 'TOP')}
                                className="block w-full text-left px-3 py-1 text-sm text-green-600 hover:bg-green-50"
                              >
                                TOP
                              </button>
                              <button 
                                onClick={() => handleLocationChange(screen.id, 'BOTH')}
                                className="block w-full text-left px-3 py-1 text-sm text-purple-600 hover:bg-purple-50"
                              >
                                BOTH
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="relative group">
                          <button className="text-gray-600 hover:text-gray-900 text-sm px-2 py-1 border border-gray-300 rounded">
                            {t('navigation.actions')} ▼
                          </button>
                          <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-300 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                            <div className="py-1">
                              <button 
                                onClick={() => toast.success(`Edit Screen: ${screen.name} (Feature coming soon)`)}
                                className="block w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50"
                              >
                                {t('navigation.edit')}
                              </button>
                              <button 
                                onClick={() => handleDeactivateScreen(screen.id, screen.name)}
                                className="block w-full text-left px-4 py-2 text-sm text-orange-600 hover:bg-orange-50"
                              >
                                {t('navigation.makeInactive')}
                              </button>
                              <button 
                                onClick={() => handleDeleteScreen(screen.id, screen.name)}
                                className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                              >
                                {t('navigation.hardDelete')}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-secondary-font mb-2">{screen.description}</p>
                    <p className="text-xs text-gray-500">{t('navigation.route')}: {screen.route_path}</p>
                    <p className="text-xs text-gray-500">{t('navigation.order')}: {screen.display_order}</p>
                    
                                         {/* Nodes */}
                     <div className="ml-4 mt-2 space-y-1">
                       {Object.values(screen.nodes)
                         .sort((a, b) => a.display_order - b.display_order)
                         .map((node) => (
                         <div key={node.id} className="flex items-center justify-between">
                           <div>
                             <span className="text-sm text-primary-font">{node.name}</span>
                             {node.description && (
                               <p className="text-xs text-gray-500">{node.description}</p>
                             )}
                             <p className="text-xs text-gray-500">{t('navigation.action')}: {node.action_type} | {t('navigation.order')}: {node.display_order}</p>
                           </div>
                           <div className="flex space-x-2">
                            <button 
                              onClick={() => toast.success(`Edit Node: ${node.name} (Feature coming soon)`)}
                              className="text-blue-600 hover:text-blue-900 text-xs"
                            >
                              {t('navigation.edit')}
                            </button>
                            <button 
                              onClick={() => handleDeleteNode(node.id, node.name)}
                              className="text-red-600 hover:text-red-900 text-xs"
                            >
                              {t('navigation.delete')}
                            </button>
                          </div>
                         </div>
                       ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        
        {/* Debug Info */}
        <div className="mt-4 p-4 bg-gray-100 rounded">
          <p className="text-sm text-gray-600">
            Debug: Total Items: {totalItems}, Items Per Page: {itemsPerPage}, 
            Current Page: {currentPage}, Total Pages: {totalPages}
          </p>
        </div>
        
        {/* Pagination */}
        {totalItems > 0 && (
          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              showPageSizeSelector={true}
              showItemCount={true}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default NavigationHierarchyPage;
